import { jsx as _jsx } from "react/jsx-runtime";
import { useReducer, useCallback, useMemo, } from 'react';
/**
 * Create a Provider listen to the data state data changes and update the children. This provider is generic and can be used
 * for any type of global data state.
 */
export const GenericGlobalDataStateProvider = ({ context, stateReducer, initialState, onLoading, onError, children, }) => {
    const [state, dispatch] = useReducer(stateReducer, initialState);
    /**
     * Implement the ExtDispatch function. ExtDispatch will take an action and check if it is a function.
     * If action is a function, it will assume the function is a ThunkAction and will call the function
     * and return the result from the function. If the action IS NOT a function, we will assume the action
     * is a normal Action and will use React's dispatch to dispatch it which will be handled by the
     * reducer.
     */
    // eslint-disable-next-line no-redeclare
    const dispatchInterceptor = useCallback(async (action, showLoading, suppressError) => {
        if (action instanceof Function) {
            // action is a ThunkAction. Call the function and return the result.
            try {
                // Pre API call setup
                // By default, we will show the loading overlay to block user interactions. To force this action NOT to block the
                // screen, pass 'false' for 'showLoading' param.
                if (showLoading !== false) {
                    onLoading(true);
                }
                const thunkAction = action;
                return await thunkAction(dispatchInterceptor);
            }
            catch (error) {
                // Post API call error handler
                // By default we WILL show errors. But sometime we don't want to show because we expected errors to occur so if this is the case,
                // pass 'true' for suppressError param to suppress errors for this action.
                if (suppressError !== true) {
                    onError(error);
                }
                // eslint-disable-next-line no-console
                console.error(error);
                throw error;
            }
            finally {
                // Post API call handler
                if (showLoading !== false) {
                    onLoading(false);
                }
            }
        }
        // action is a normal Action, use React.dispatch to dispatch it.
        return dispatch(action);
    }, [onError, onLoading]);
    const contextValue = useMemo(() => {
        return {
            state,
            dispatch: dispatchInterceptor,
        };
    }, [state, dispatchInterceptor]);
    // Instead of using the 'dispatch' from the context, we will use dispatchInterceptor to intercept the dispatch call and handle the call.
    // This is how we can support Thunk actions.
    return (_jsx(context.Provider, { value: contextValue, children: children }, void 0));
};
